/* eslint-disable  @typescript-eslint/no-explicit-any */
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { DataService } from './data.service';
import { DomainService } from './domain.service';
import { PaymentGateway } from './online-payment.service';
import { PayNowAtTableRequest, PayAtTableNiPayload, PayAtTableApplePayPayload } from '../models/pay-at-table.model';
import { ApplePayConfig, ApplePayPayload, ApplePayPaymentPayload } from '../models/payment.model';
import { ApiService } from './api.service';
import { PatCheckoutConfirmService } from './pat-checkout-confirm.service';

@Injectable({
  providedIn: 'root'
})
export class ApplePayService {

  public ApplePaySuccess = new EventEmitter<boolean>();

  private transactionId: string;
  private restaurantId: string;
  private defaultPG: string;

  constructor(
    private dataService: DataService,
    private apiService: ApiService,
    private ngZone: NgZone,
    private domainService: DomainService,
    private patCheckoutConfirmService: PatCheckoutConfirmService,
  ) {
    this.restaurantId = this.dataService.restaurant().id;
    this.setupPG();
  }

  public isApplePayAvailable(): boolean {
    try {
      return (window as any).ApplePaySession && (window as any).ApplePaySession.canMakePayments();
    } catch (error) {
      return false;
    }
  }

  /**
   * Detects common in-app webviews (Instagram, Facebook, TikTok, WeChat, Line, etc.)
   * reached via a shared link or QR code. These render with Safari's WebKit engine
   * so they look like a normal browser, but don't expose ApplePaySession — the most
   * common reason Apple Pay "sometimes shows, sometimes doesn't" for the same user.
   */
  public isInAppBrowser(): boolean {
    const ua = (window.navigator?.userAgent || '').toLowerCase();
    const signatures = ['fban', 'fbav', 'instagram', 'tiktok', 'musical_ly', 'micromessenger', 'line/'];
    return signatures.some((signature) => ua.includes(signature));
  }

  public startApplePaySession(appleSession, orderIds: string[], payNowRequest: PayNowAtTableRequest | null = null, tipAmount: number | 0 = 0) {
    this.handleApplePayEvents(appleSession, orderIds, payNowRequest, tipAmount);
    appleSession.begin();
  }

  /** Builds the merchant config from the order's currency/amount and constructs the native session. */
  public createApplePaySession(currency: string, amount: number) {
    const config = this.buildApplePayConfig(currency, amount);
    return new (window as any).ApplePaySession(6, config);
  }

  public startPayAtTableApplePaySession(appleSession, payload: PayAtTableNiPayload, voucherName?: string) {
    this.handlePayAtTableApplePayEvents(appleSession, payload, voucherName);
    appleSession.begin();
  }

  public getTransactionId(): string {
    return this.transactionId;
  }

  private handleApplePayEvents(
    appleSession,
    orderIds: string[],
    payNowRequest: PayNowAtTableRequest | null = null,
    tipAmount: number,
  ) {
    let paymentSuccess = false;

    appleSession.onvalidatemerchant = (event) => {
      this.validateApplePaySession(event.validationURL, (merchantSession) => {
        appleSession.completeMerchantValidation(merchantSession);
      });
    };

    appleSession.onpaymentauthorized = (event) => {
      this.performTransaction(event.payment, orderIds, payNowRequest, tipAmount, (outcome) => {
        if (outcome === 'SUCCESS') {
          appleSession.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
          paymentSuccess = true;
        } else {
          appleSession.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
        }

        // This is because we are triggering a navigation from within a global callback
        this.ngZone.run(() => {
          // Added 3 seconds wait until the apple pay sheet disappears
          setTimeout(() => {
            this.ApplePaySuccess.emit(paymentSuccess);
          }, 3000);
        });
      });
    };
  }

  private handlePayAtTableApplePayEvents(appleSession, payload: PayAtTableNiPayload, voucherName?: string) {
    let paymentSuccess = false;

    appleSession.onvalidatemerchant = (event) => {
      this.validateApplePaySession(event.validationURL, (merchantSession) => {
        appleSession.completeMerchantValidation(merchantSession);
      });
    };

    appleSession.onpaymentauthorized = (event) => {
      this.performPayAtTableTransaction(event.payment, payload, voucherName, (outcome) => {
        if (outcome === 'SUCCESS') {
          appleSession.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
          paymentSuccess = true;
        } else {
          appleSession.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
        }

        this.ngZone.run(() => {
          setTimeout(() => {
            this.ApplePaySuccess.emit(paymentSuccess);
          }, 3000);
        });
      });
    };
  }

  private validateApplePaySession(appleUrl, callback) {
    const payload: ApplePayPayload = {
      restaurant_id: this.dataService.restaurant().id,
      applepay_url: appleUrl,
      subdomain: this.domainService.getSubdomain()
    };
    this.apiService.getApplePaySession(payload).subscribe(response => {
      callback(response);
    });
  }

  private performTransaction(
    details,
    orderIds: string[],
    payNowRequest: PayNowAtTableRequest | null = null,
    tipAmount: number,
    callback
  ) {
    const payload: ApplePayPaymentPayload = {
      applepay: true,
      subdomain: this.domainService.getSubdomain(),
      order_ids: orderIds,
      tip_amount: tipAmount,
      applepay_data: { token: JSON.stringify(details.token.paymentData) }
    };

    if (this.defaultPG === PaymentGateway.Payfort) {
      const payfortPayload = {
        order_ids: orderIds,
        applepay_data: details
      };
      this.apiService.submitApplePayPayfort(payfortPayload, this.restaurantId).subscribe(res => {
        if (res.result === 'SUCCESS') {
          this.transactionId = res.order.id.substr(4);
        }
        callback(res.result);
      });
    } else if (this.defaultPG === PaymentGateway.TotalProcessing) {
      this.apiService.submitApplePayTP(payload).subscribe(res => {
        if (res.result === 'SUCCESS') {
          this.transactionId = res.order.id.substr(4);
        }
        callback(res.result);
      });
    } else {
      if (payNowRequest) {
        payload.payment_mode = "custom_amount";
        payload.cheque_id = payNowRequest.cheque_id;
        payload.payment_details = {
          amount: payNowRequest.total_amount,
        };
      }
      this.apiService.getNiSession(payload).subscribe(res => {
        if (res.result === 'SUCCESS') {
          this.transactionId = res.order.id.substr(4);
        }
        callback(res.result);
      });
    }
  }

  private performPayAtTableTransaction(details, payload: PayAtTableNiPayload, voucherName: string | undefined, callback) {
    const payAtTablePayload: PayAtTableApplePayPayload = {
      ...payload,
      applepay: true,
      applepay_data: { token: JSON.stringify(details.token.paymentData) }
    };

    // Persist fees + voucher on the cheque before charging: the NI session
    // prices from the cheque's stored discount, so paying without this
    // confirm charges the undiscounted total. On confirm failure the payment
    // must fail — proceeding would overcharge the card.
    this.patCheckoutConfirmService.confirm(payload, voucherName).subscribe(confirmed => {
      if (!confirmed) {
        callback('FAIL');
        return;
      }

      this.apiService.getNiSession(payAtTablePayload).subscribe({
        next: (res) => {
          if (res.result === 'SUCCESS') {
            this.transactionId = res.order.id.substr(4);
          }
          callback(res.result);
        },
        error: () => callback('FAIL'),
      });
    });
  }

  private buildApplePayConfig(currency: string, amount: number): ApplePayConfig {
    return {
      countryCode: currency === 'SAR' ? 'SA' : 'AE',
      currencyCode: currency,
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover', 'mada'],
      merchantCapabilities: ['supports3DS'],
      total: { label: 'Opaala', amount },
    };
  }

  private setupPG(): void {
    if (!this.restaurantId) {
      return;
    }
    const methods = this.dataService.paymentMethods();
    const method = methods?.find(m => m.is_default_payment_gateway === true);
    if (!method) {
      return;
    } else {
      this.defaultPG = method.key;
    }
  }
}
